/**
 * 统一矢量图标库（替代 emoji）。
 * 全部采用与道具 SVG 一致的描边风格（currentColor，便于随文字色变化）。
 * 用法：<Icon name="candle" size={24} className="text-ink-900" />
 */
import type { CSSProperties } from 'react';

const PATHS: Record<string, string> = {
  candle: `<rect x="24" y="24" width="16" height="30" rx="2"/><path d="M32 24v-5"/><path d="M32 8c4 4 4 8 0 12-4-4-4-8 0-12z"/>`,
  lock: `<rect x="20" y="28" width="24" height="22" rx="3"/><path d="M26 28v-4a6 6 0 0 1 12 0v4"/><path d="M32 35v6"/><circle cx="32" cy="34" r="1.5"/>`,
  dove: `<path d="M14 40c10 0 18-6 22-16 2 8 8 10 14 8-4 10-14 18-28 18-6 0-10-2-12-6 4 0 8-2 10-6z"/><path d="M34 24c2-2 6-2 8 0"/>`,
  globe: `<circle cx="32" cy="32" r="20"/><path d="M12 32h40"/><path d="M32 12c5 6 5 18 0 24M32 12c-5 6-5 18 0 24"/><path d="M15 22c8 4 26 4 34 0M15 42c8-4 26-4 34 0"/>`,
  bolt: `<path d="M34 8 18 36h12l-4 20 18-30H32z"/>`,
  shield: `<path d="M32 10 14 19v13c0 11 8 17 18 21 10-4 18-10 18-21V19z"/><path d="M24 32l6 6 10-12"/>`,
  moon: `<path d="M40 14a18 18 0 1 0 12 32A22 22 0 0 1 40 14z"/>`,
  chain: `<path d="M27 39a8 8 0 0 1-11-11l6-6a8 8 0 0 1 11 11"/><path d="M37 25a8 8 0 0 1 11 11l-6 6a8 8 0 0 1-11-11"/><path d="M26 26l12 12"/>`,
  box: `<path d="M32 12 12 22v20l20 10 20-10V22z"/><path d="M12 22l20 10 20-10"/><path d="M32 32v20"/>`,
  medal: `<circle cx="32" cy="26" r="12"/><path d="M23 36l-7 18 14-9 14 9-7-18"/>`,
  card: `<rect x="12" y="20" width="40" height="26" rx="3"/><path d="M12 28h40"/><path d="M18 40h12"/>`,
  image: `<rect x="12" y="16" width="40" height="32" rx="3"/><circle cx="24" cy="28" r="4"/><path d="M14 44l12-12 8 8 6-6 10 10"/>`,
  close: `<path d="M22 22l20 20M42 22L22 42"/>`,
  check: `<path d="M18 34l10 10 18-22"/>`,
  person: `<circle cx="32" cy="24" r="10"/><path d="M14 52c0-10 8-16 18-16s18 6 18 16"/>`,
  flower: `<circle cx="32" cy="32" r="4"/><path d="M32 32V14M32 32L48 20M32 32L50 32M32 32L48 44M32 32L32 50M32 32L16 44M32 32L14 32M32 32L16 20"/>`,
  star: `<path d="M32 10l5 14 14 1-11 9 4 14-12-8-12 8 4-14-11-9 14-1z"/>`,
  firefly: `<circle cx="22" cy="26" r="3"/><circle cx="42" cy="22" r="2.5"/><circle cx="44" cy="42" r="3"/><circle cx="24" cy="44" r="2"/><path d="M22 26l-6-3M42 22l6-3M44 42l6 3M24 44l-6 3"/>`,
  chart: `<path d="M12 48V22M28 48V30M44 48V14"/><path d="M10 52h44"/>`,
  user: `<circle cx="32" cy="22" r="10"/><path d="M16 50c0-9 7-14 16-14s16 5 16 14"/>`,
  building: `<path d="M16 52V20l16-10 16 10v32"/><path d="M16 20h32"/><path d="M24 52V36h4v16M36 52V36h4v16"/><path d="M28 28h8v6h-8z"/>`,
  palette: `<path d="M32 12a20 20 0 1 0 0 40c4 0 6-3 6-6 0-3 3-4 6-4h2a8 8 0 0 0 8-8c0-12-10-22-22-22z"/><circle cx="24" cy="26" r="2"/><circle cx="34" cy="22" r="2"/><circle cx="43" cy="30" r="2"/>`,
  gear: `<circle cx="32" cy="32" r="9"/><path d="M32 12v6M32 46v6M12 32h6M46 32h6M18 18l4 4M42 42l4 4M46 18l-4 4M22 42l-4 4"/>`,
  tags: `<path d="M14 14h20l16 16-20 20L14 34z"/><circle cx="22" cy="22" r="3"/>`,
  code: `<path d="M24 20 12 32l12 12M40 20l12 12-12 12M36 16l-8 32"/>`,
  list: `<circle cx="12" cy="18" r="2"/><circle cx="12" cy="32" r="2"/><circle cx="12" cy="46" r="2"/><path d="M20 18h28M20 32h28M20 46h28"/>`,
};

export function Icon({
  name,
  size = 24,
  className,
  style,
}: {
  name: string;
  size?: number;
  className?: string;
  style?: CSSProperties;
}) {
  const inner = PATHS[name];
  if (!inner) return null;
  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: inner }}
    />
  );
}
